import React, { useState, useEffect } from "react";
import { Spinner, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import ReactSwitch from "react-switch";
import moment from "moment";
import "./GlassmorphicFilters.scss";

const CalendarGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="16" rx="3" />
    <path d="M16 3v4M8 3v4M3 10h18" />
  </svg>
);

const CheckGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const ChevronGlyph = () => (
  <svg className="fill-current h-3.5 w-3.5" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
  </svg>
);

// Selector de día/mes/año local, propio del look del panel de filtros.
// Duplica intencionalmente la lógica mínima de DateDropdown para no
// depender de ese componente compartido ni afectar otras integraciones.
const GlassDateField = ({ isOpen, toggle, label, items }) => (
  <Dropdown isOpen={isOpen} toggle={toggle}>
    <DropdownToggle className="bg-transparent border-0 shadow-none p-0 m-0 text-sm font-medium text-[#1D1F3F] hover:bg-transparent focus:outline-none flex items-center gap-1">
      {label}
      <i className="mdi mdi-chevron-down text-xs text-slate-400"></i>
    </DropdownToggle>
    <DropdownMenu className="dropdown-scrollable">{items}</DropdownMenu>
  </Dropdown>
);

const GlassDateGroup = ({ dateRange, setDate, end }) => {
  const maxYear = new Date().getFullYear();
  const years = [];
  for (let i = maxYear; i >= 2000; i--) years.push(i);

  const [openDay,   setOpenDay]   = useState(false);
  const [openMonth, setOpenMonth] = useState(false);
  const [openYear,  setOpenYear]  = useState(false);

  const date = end ? dateRange[1] : dateRange[0];

  const handleSetDate = (d) => {
    if (end) {
      setDate([dateRange[0], d]);
    } else {
      setDate([d, dateRange[1]]);
    }
  };

  // Build day list for the current month/year — avoids Feb 30 etc.
  const daysInMonth = date.daysInMonth();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <>
      <GlassDateField
        isOpen={openDay}
        toggle={() => setOpenDay(!openDay)}
        label={date.format("D")}
        items={days.map((d) => (
          <DropdownItem key={d} onClick={() => handleSetDate(date.clone().date(d))}>
            {d}
          </DropdownItem>
        ))}
      />
      <GlassDateField
        isOpen={openMonth}
        toggle={() => setOpenMonth(!openMonth)}
        label={date.format("MMMM")}
        items={moment.months().map((month, i) => {
          // Snap day to last valid day of new month to avoid e.g. Feb 31
          const candidate = date.clone().month(i);
          const snapped   = candidate.date(Math.min(date.date(), candidate.daysInMonth()));
          return (
            <DropdownItem key={i} onClick={() => handleSetDate(snapped)}>
              {month}
            </DropdownItem>
          );
        })}
      />
      <GlassDateField
        isOpen={openYear}
        toggle={() => setOpenYear(!openYear)}
        label={date.format("YYYY")}
        items={years.map((year) => {
          // Snap day to last valid day of new year/month combination
          const candidate = date.clone().year(year);
          const snapped   = candidate.date(Math.min(date.date(), candidate.daysInMonth()));
          return (
            <DropdownItem key={year} onClick={() => handleSetDate(snapped)}>
              {year}
            </DropdownItem>
          );
        })}
      />
    </>
  );
};

const GlassmorphicFilters = ({
  loadingBtn,
  dateRange,
  useMockData,
  setUseMockData,
  onWeeklyReport,
  onMonthlyReport,
  onCustomRangeReport,
  areaData = [],
  selectedArea,
  setSelectedArea,
  diseaseData = [],
  selectedDisease = "",
  setSelectedDisease = () => {},
}) => {
  // Local "pending" state: month/year pickers update this, not the hook directly.
  // Only committed to the hook when the user clicks "Aplicar".
  const [pendingDateRange, setPendingDateRange] = useState(dateRange);

  // Keep pickers in sync when Semanal/Mensual updates the active range from outside.
  useEffect(() => {
    setPendingDateRange(dateRange);
  }, [dateRange]);
  // El switch de datos demo solo existe para la empresa de pruebas (empresaId === 1)
  const [canUseMockToggle] = useState(() => {
    try {
      return sessionStorage.getItem("empresaId") === "1";
    } catch {
      return false;
    }
  });

  return (
    <div className="glass-filters-card p-4 sm:p-5 w-full text-left">
      {canUseMockToggle && (
        <div className="flex justify-end mb-3">
          <label className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full pl-3 pr-1.5 py-1 cursor-pointer select-none">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 font-display">
              {useMockData ? "Datos demo" : "Datos reales"}
            </span>
            <ReactSwitch
              checked={useMockData}
              onChange={() => setUseMockData(!useMockData)}
              onColor="#007FB4"
              offColor="#cbd5e1"
              width={38}
              height={20}
              handleDiameter={16}
              uncheckedIcon={false}
              checkedIcon={false}
              boxShadow="none"
              activeBoxShadow="none"
            />
          </label>
        </div>
      )}

      {/* Rangos rápidos + Área */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-display">
            Rangos rápidos
          </span>
          <div className="flex gap-2">
            <button
              onClick={onWeeklyReport}
              disabled={loadingBtn === 1}
              className="flex-1 h-10 flex items-center justify-center gap-1.5 px-3 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl text-slate-700 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#007FB4]/40"
            >
              {loadingBtn === 1 ? <Spinner size="sm" color="secondary" /> : <CalendarGlyph />}
              Semanal
            </button>
            <button
              onClick={onMonthlyReport}
              disabled={loadingBtn === 2}
              className="flex-1 h-10 flex items-center justify-center gap-1.5 px-3 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl text-slate-700 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#007FB4]/40"
            >
              {loadingBtn === 2 ? <Spinner size="sm" color="secondary" /> : <CalendarGlyph />}
              Mensual
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-display">
            Área
          </span>
          <div className="relative">
            <select
              aria-label="Área"
              value={selectedArea ? (selectedArea.id || selectedArea.AreaId || "") : ""}
              onChange={(e) => {
                const val = e.target.value;
                if (!val) {
                  setSelectedArea(undefined);
                } else {
                  const area = areaData.find((a) => String(a.id || a.AreaId) === String(val));
                  setSelectedArea(area);
                }
              }}
              disabled={areaData.length === 0}
              className="h-10 w-full appearance-none cursor-pointer bg-white border border-slate-200 rounded-xl pl-3 pr-8 text-sm font-medium text-slate-700 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#007FB4]/40"
            >
              <option value="">Todas las áreas</option>
              {areaData.map((area) => (
                <option key={area.id || area.AreaId} value={area.id || area.AreaId}>
                  {area.name || area.Nombre || `Área ${area.id || area.AreaId}`}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-slate-400">
              <ChevronGlyph />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1.5 mt-3">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-display">
          Enfermedad
        </span>
        <div className="relative">
          <select
            aria-label="Enfermedad"
            value={selectedDisease}
            onChange={(e) => setSelectedDisease(e.target.value)}
            disabled={diseaseData.length === 0}
            className="h-10 w-full appearance-none cursor-pointer bg-white border border-slate-200 rounded-xl pl-3 pr-8 text-sm font-medium text-slate-700 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#007FB4]/40"
          >
            <option value="">Todas las enfermedades</option>
            {diseaseData.map((disease) => {
              const name = typeof disease === "string"
                ? disease
                : disease.name || disease.Nombre;
              return name ? (
                <option key={name} value={name}>
                  {name}
                </option>
              ) : null;
            })}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-slate-400">
            <ChevronGlyph />
          </div>
        </div>
      </div>

      {/* Rango personalizado - ancho completo */}
      <div className="flex flex-col gap-1.5 mt-3 pt-3 border-t border-slate-200">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-display">
          Rango personalizado
        </span>
        <div className="flex items-stretch gap-2">
          <div className="h-10 flex items-center gap-1.5 flex-1 bg-white border border-slate-200 rounded-xl px-3 min-w-0">
            <GlassDateGroup dateRange={pendingDateRange} setDate={setPendingDateRange} />
            <span className="text-slate-400 font-semibold px-0.5">–</span>
            <GlassDateGroup dateRange={pendingDateRange} setDate={setPendingDateRange} end />
          </div>
          <button
            onClick={() => onCustomRangeReport(pendingDateRange)}
            disabled={loadingBtn === 3}
            className="h-10 flex items-center justify-center gap-1.5 px-4 bg-[#1D1F3F] hover:bg-[#2e3160] rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#007FB4]/40"
          >
            {loadingBtn === 3 ? <Spinner size="sm" color="light" /> : <CheckGlyph />}
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
};

export default GlassmorphicFilters;
